<script lang="ts">
  /**
   * The square coloured badge at the start of a settings row: a fixed colour
   * with a glyph on it, the same in the dark and the light appearance. The
   * colour belongs to the mark, not to the chrome, which is why the seven
   * tones are tokens of their own (`--ui-badge-*`) rather than ladder steps.
   *
   * The glyph is one of the shell's (`glyph`, an `IconName`, drawn at 18px)
   * or the game's own (`children`, an 18px drawing in `currentColor`).
   *
   * Decoration (`aria-hidden`): the row's label says what the row is.
   *
   * Test id: `testid`. No default.
   */
  import type { Snippet } from 'svelte';

  import type { RowBadgeTone } from '../types.js';
  import type { IconName } from '../icons.js';
  import Icon from './Icon.svelte';

  interface Props {
    readonly tone: RowBadgeTone;
    /** One of the shell's glyphs. */
    readonly glyph?: IconName | undefined;
    /** A glyph of the game's own, 18px, in `currentColor`; used when `glyph` is absent. */
    readonly children?: Snippet | undefined;
    readonly testid?: string | undefined;
  }
  const { tone, glyph, children, testid }: Props = $props();
</script>

<span class="ui-row-badge ui-row-badge--{tone}" aria-hidden="true" data-testid={testid}>
  {#if glyph !== undefined}
    <Icon name={glyph} size={18} />
  {:else if children !== undefined}
    {@render children()}
  {/if}
</span>

<style>
  .ui-row-badge {
    flex: 0 0 auto;
    width: 2rem;
    height: 2rem;
    border-radius: var(--ui-radius-sm);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--ui-badge-ink);
    /* A filled glyph's inner mark is cut in the badge's own colour. */
    background: var(--ui-icon-cut);
  }

  .ui-row-badge :global(svg) {
    width: 18px;
    height: 18px;
  }

  .ui-row-badge--indigo {
    --ui-icon-cut: var(--ui-badge-indigo);
  }

  .ui-row-badge--orange {
    --ui-icon-cut: var(--ui-badge-orange);
  }

  .ui-row-badge--green {
    --ui-icon-cut: var(--ui-badge-green);
  }

  .ui-row-badge--red {
    --ui-icon-cut: var(--ui-badge-red);
  }

  .ui-row-badge--purple {
    --ui-icon-cut: var(--ui-badge-purple);
  }

  .ui-row-badge--blue {
    --ui-icon-cut: var(--ui-badge-blue);
  }

  .ui-row-badge--teal {
    --ui-icon-cut: var(--ui-badge-teal);
  }
</style>
